import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '@/components/base/Button';
import Card from '@/components/base/Card';
import EmptyState from '@/components/base/EmptyState';
import ErrorState from '@/components/base/ErrorState';
import Skeleton from '@/components/base/Skeleton';
import Toast from '@/components/base/Toast';
import { createDriver, fetchDrivers } from '@/services/driversService';
import type { CreateDriverPayload, Driver, DriverProfilePayload } from '@/types';
import DriverFormModal from './components/DriverFormModal';
import DriversTable from './components/DriversTable';

type ToastState = { message: string; tone: 'success' | 'error' };

export default function DriversPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [drivers, setDrivers] = useState<Driver[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);

  // Show a toast passed from the detail page (e.g. after deleting a driver).
  useEffect(() => {
    const state = location.state as { toast?: string } | null;
    if (state?.toast) {
      setToast({ message: state.toast, tone: 'success' });
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await fetchDrivers();
      setDrivers(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const notify = (message: string, tone: ToastState['tone'] = 'success') =>
    setToast({ message, tone });

  const handleSubmit = async (payload: CreateDriverPayload | DriverProfilePayload, id: string | null) => {
    if (id) {
      return;
    }
    const created = await createDriver(payload as CreateDriverPayload);
    setDrivers((prev) => (prev ? [created, ...prev] : [created]));
    notify(t('drivers.toast.created'));
  };

  const isEmpty = !loading && !error && drivers !== null && drivers.length === 0;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-foreground-950 md:text-xl">{t('drivers.title')}</h1>
          <p className="mt-1 text-sm text-foreground-500">{t('drivers.subtitle')}</p>
        </div>
        <Button variant="primary" size="md" icon="ri-add-line" onClick={() => setFormOpen(true)}>
          {t('drivers.add')}
        </Button>
      </header>

      <Card padded={false} className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200 px-4 py-4 md:px-5">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
              <i className="ri-e-bike-2-line text-xl leading-none" aria-hidden="true" />
            </span>
            <div>
              <h2 className="text-base font-semibold text-foreground-950">{t('drivers.title')}</h2>
              <p className="mt-0.5 text-xs text-foreground-500">{t('drivers.scopeNote')}</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3 p-4 md:p-5">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-11 w-11 rounded-full" />
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-5 flex-1" />
                <Skeleton className="h-8 w-24" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="p-4 md:p-5">
            <ErrorState
              title={t('drivers.error')}
              description={t('common.errorText')}
              retryLabel={t('common.retry')}
              onRetry={load}
            />
          </div>
        ) : isEmpty ? (
          <div className="p-4 md:p-5">
            <EmptyState
              icon="ri-e-bike-2-line"
              title={t('drivers.empty.title')}
              description={t('drivers.empty.text')}
              action={
                <Button variant="primary" size="md" icon="ri-add-line" onClick={() => setFormOpen(true)}>
                  {t('drivers.empty.action')}
                </Button>
              }
            />
          </div>
        ) : (
          <DriversTable
            drivers={drivers ?? []}
            onView={(driver) => navigate(`/dashboard/drivers/${driver.id}`)}
          />
        )}

        {!loading && !error && drivers && drivers.length > 0 ? (
          <div className="border-t border-background-100 bg-background-100/60 px-4 py-3 md:px-5">
            <p className="flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
              <i className="ri-information-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
              {t('drivers.demoNote')}
            </p>
          </div>
        ) : null}
      </Card>

      {/* Availability legend */}
      <Card>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-100 text-foreground-500">
              <i className="ri-information-line text-lg" aria-hidden="true" />
            </span>
            <div>
              <p className="text-sm font-semibold text-foreground-900">{t('drivers.legend.title')}</p>
              <p className="text-xs text-foreground-500">{t('drivers.legend.text')}</p>
            </div>
          </div>
          <ul className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-600">
            <li className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" aria-hidden="true" />
              {t('drivers.legend.available')}
            </li>
            <li className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-amber-500" aria-hidden="true" />
              {t('drivers.legend.busy')}
            </li>
            <li className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-foreground-300" aria-hidden="true" />
              {t('drivers.legend.unavailable')}
            </li>
          </ul>
        </div>
      </Card>

      <DriverFormModal
        open={formOpen}
        driver={null}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
      />

      {toast ? <Toast message={toast.message} tone={toast.tone} onClose={() => setToast(null)} /> : null}
    </div>
  );
}